import { Button, Skeleton } from '@personachat/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LockOpen, Unlock, X } from 'lucide-react';
import { useState } from 'react';

import { api } from '../../api/client';
import { lire } from '../../api/call';
import type { components } from '../../api/schema';
import { ErreurChargement } from '../../components/bits';
import { ConfirmDialog } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../lib/format';

type Fiche = components['schemas']['UserDetail'];

interface Cible {
  id: string;
  name: string;
  action: 'debloquer' | 'retirer';
}

/** Onglet Coachs : coachs par module, débloquer ou retirer un coach, tout débloquer. */
export function CoachesTab({ eleve }: { eleve: Fiche }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [cible, setCible] = useState<Cible | null>(null);
  const [tout, setTout] = useState(false);

  const coachs = useQuery({
    queryKey: ['eleve', eleve.id, 'coachs'],
    queryFn: () => lire(api.GET('/api/admin/users/{id}/coaches', { params: { path: { id: eleve.id } } })),
  });

  const rafraichir = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['eleve', eleve.id] }),
      queryClient.invalidateQueries({ queryKey: ['audit', eleve.id] }),
    ]);

  if (coachs.isError) return <ErreurChargement erreur={coachs.error} onReessayer={() => void coachs.refetch()} />;
  if (!coachs.data) {
    return (
      <div className="stack">
        <Skeleton height={120} />
        <Skeleton height={120} />
      </div>
    );
  }

  const nbDebloques = coachs.data.modules.reduce((t, m) => t + m.coaches.filter((c) => c.unlocked).length, 0);

  return (
    <>
      <div className="row row--between" style={{ marginBottom: 12 }}>
        <span className="muted">
          {nbDebloques} coach{nbDebloques > 1 ? 's' : ''} débloqué{nbDebloques > 1 ? 's' : ''}. L’application n’affiche que les modules du type de
          compte de l’élève.
        </span>
        <Button size="sm" icon={<LockOpen size={14} aria-hidden="true" />} onClick={() => setTout(true)}>
          Tout débloquer
        </Button>
      </div>

      {coachs.data.modules.map((m) => (
        <div key={m.id} className="coach-module">
          <div className="coach-module__header">
            <span>
              {m.name}
              {!m.isActive && <span className="muted small"> · module désactivé</span>}
            </span>
            <span className="muted small">
              {m.coaches.filter((c) => c.unlocked).length} / {m.coaches.length}
            </span>
          </div>
          {m.coaches.map((c) => (
            <div key={c.id} className={c.unlocked ? 'coach-row' : 'coach-row coach-row--locked'}>
              <span className="coach-row__name">
                {c.name}
                {!c.isActive && <span className="muted small"> · désactivé</span>}
              </span>
              {c.unlocked ? (
                <>
                  <span className="muted small nowrap">
                    Débloqué le {formatDate(c.unlockedAt)}
                    {c.unlockKey && (
                      <>
                        {' '}
                        avec <code>{c.unlockKey.key}</code>
                      </>
                    )}
                  </span>
                  <Button size="sm" variant="ghost" icon={<X size={14} aria-hidden="true" />} onClick={() => setCible({ id: c.id, name: c.name, action: 'retirer' })}>
                    Retirer
                  </Button>
                </>
              ) : (
                <Button size="sm" icon={<Unlock size={14} aria-hidden="true" />} onClick={() => setCible({ id: c.id, name: c.name, action: 'debloquer' })}>
                  Débloquer
                </Button>
              )}
            </div>
          ))}
        </div>
      ))}

      <ConfirmDialog
        open={!!cible}
        onOpenChange={(o) => !o && setCible(null)}
        title={cible?.action === 'retirer' ? 'Retirer un coach' : 'Débloquer un coach'}
        confirmLabel={cible?.action === 'retirer' ? 'Retirer' : 'Débloquer'}
        tone={cible?.action === 'retirer' ? 'danger' : 'primary'}
        onConfirm={async () => {
          if (!cible) return;
          const r =
            cible.action === 'retirer'
              ? await lire(api.DELETE('/api/admin/users/{id}/unlocks/{personalityId}', { params: { path: { id: eleve.id, personalityId: cible.id } } }))
              : await lire(api.POST('/api/admin/users/{id}/unlocks', { params: { path: { id: eleve.id } }, body: { personalityId: cible.id } }));
          await Promise.all([coachs.refetch(), rafraichir()]);
          toast.succes(r.message ?? 'Fait.');
        }}
      >
        {cible?.action === 'retirer' ? (
          <p>
            <strong>{cible.name}</strong> ne sera plus accessible à {eleve.email}. Ses conversations avec ce coach sont conservées.
          </p>
        ) : (
          <p>
            <strong>{cible?.name}</strong> devient accessible à {eleve.email}.
          </p>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={tout}
        onOpenChange={setTout}
        title="Débloquer tous les coachs"
        confirmLabel="Tout débloquer"
        onConfirm={async () => {
          const r = await lire(api.POST('/api/admin/users/{userId}/unlock-all', { params: { path: { userId: eleve.id } } }));
          await Promise.all([coachs.refetch(), rafraichir()]);
          toast.succes(r.newUnlocks > 0 ? `${r.newUnlocks} coach(s) débloqué(s).` : 'Tous les coachs étaient déjà débloqués.');
        }}
      >
        <p>
          Tous les coachs actifs, de tous les modules, deviennent accessibles à <strong>{eleve.email}</strong>. À réserver aux cas particuliers :
          le parcours normal débloque les coachs un par un.
        </p>
      </ConfirmDialog>
    </>
  );
}
